import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  text:"#1a1a2e", muted:"#6b7280", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"10px 13px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

// Signature Pad Component
function SignaturePad({ onSign, height = 150 }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    const x = (touch ? touch.clientX : e.clientX) - rect.left;
    const y = (touch ? touch.clientY : e.clientY) - rect.top;
    return { x: x * (canvas.width / rect.width), y: y * (canvas.height / rect.height) };
  };

  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stop = () => {
    setIsDrawing(false);
    if (hasSignature) {
      onSign(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSign(null);
  };

  return (
    <div>
      <div style={{position:"relative",border:`2px dashed ${C.borderGold}`,borderRadius:8,background:"#fff",overflow:"hidden"}}>
        <canvas
          ref={canvasRef}
          width={600}
          height={height}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
          style={{width:"100%",height,touchAction:"none",cursor:"crosshair"}}
        />
        {!hasSignature && (
          <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",color:C.muted,fontSize:13,pointerEvents:"none",fontStyle:"italic"}}>Sign here with mouse or finger</div>
        )}
      </div>
      <button onClick={clear} style={{marginTop:8,padding:"6px 12px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>Clear</button>
    </div>
  );
}

// Consent Signing Flow
export function ConsentSigningFlow({ client, appointment, onComplete, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [signature, setSignature] = useState(null);
  const [patientName, setPatientName] = useState(client?.name || "");
  const [signedList, setSignedList] = useState([]);
  const [step, setStep] = useState("select");
  // Templates the client has ALREADY signed in the past — we skip them so
  // they never re-sign the same consent twice. Persists across visits.
  const [skippedAlreadySigned, setSkippedAlreadySigned] = useState([]);
  // ★ ALL active consent templates (for the manual "Add Consent" picker).
  //   Lets staff pull in ANY consent on the fly during check-in, even one
  //   that wasn't auto-matched to the appointment's services.
  const [allActiveTemplates, setAllActiveTemplates] = useState([]);
  const [showAddConsent, setShowAddConsent] = useState(false);
  const [addSearch, setAddSearch] = useState("");

  useEffect(() => {
    (async () => {
      // Build the list of services on THIS appointment (multi-treatment aware:
      // "Botox + Lip Filler" → both services counted) and only show consents
      // that are tagged to at least one of them. Untagged templates are
      // treated as universal (HIPAA, general waivers) and always show.
      // ★ Multi-treatment aware — staff books "Lymphatic Massage + Nova
      //   Contour Facial" as a single appointment.treat string. Split on
      //   " + " so EACH treatment is matched independently and the patient
      //   sees a consent for EVERY service in the appointment.
      const treatListRaw = (appointment?.treat || "")
        .split(/\s*\+\s*/)
        .map(s => s.trim())
        .filter(Boolean);

      // Normalize aggressively: lowercase, strip non-alphanumeric punctuation,
      // collapse whitespace. This catches "Lymphatic Massage" vs "Lymphatic
      // massage" vs " Lymphatic-Massage " — all the same. Without this, a
      // trailing space in the DB or different capitalization between the
      // booking form and the services table caused consent silently dropped.
      const normalize = (s) => (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      const treatList = treatListRaw.map(normalize).filter(Boolean);

      const [{ data: allTemplates }, { data: links }, { data: allServices }, { data: alreadySigned }] = await Promise.all([
        supabase.from("consent_templates").select("*").eq("active", true).order("sort_order"),
        supabase.from("consent_template_services").select("*"),
        supabase.from("services").select("id,name"),
        // ★ Pull every consent this client has already signed (any visit, any
        // appointment, including AR-imported records). We use these to skip
        // re-signing the same form on return visits.
        client?.id
          ? supabase.from("signed_consents")
              .select("template_id,template_name,signed_at")
              .eq("client_id", client.id)
              .eq("status", "signed")
          : Promise.resolve({ data: [] }),
      ]);

      // Primary pass: exact normalized name match.
      const matchedServiceIds = (allServices || [])
        .filter(s => treatList.includes(normalize(s.name)))
        .map(s => s.id);

      // ★ Fallback pass — for any treatment string that DIDN'T find an
      //   exact service match, fall back to word-overlap matching using
      //   words ≥ 4 chars. Catches "Lymphatic Massage" appointment when
      //   the services table holds "Lymphatic Drainage Massage", etc.
      //   We log unmatched names so staff can fix the catalog name.
      const matchedNorms = new Set((allServices || [])
        .filter(s => matchedServiceIds.includes(s.id))
        .map(s => normalize(s.name)));
      const unmatchedTreats = treatList.filter(tn => !matchedNorms.has(tn));
      if (unmatchedTreats.length > 0) {
        console.warn("[consent] treatment names not exact-matched in services:", unmatchedTreats);
        const STOP_WORDS = new Set(["with", "and", "the", "for", "from", "into", "treatment"]);
        const wordSet = (s) => new Set(normalize(s).split(" ").filter(w => w.length >= 4 && !STOP_WORDS.has(w)));
        for (const treat of unmatchedTreats) {
          const tWords = wordSet(treat);
          if (tWords.size === 0) continue;
          for (const svc of (allServices || [])) {
            if (matchedServiceIds.includes(svc.id)) continue;
            const sWords = wordSet(svc.name);
            // At least one ≥4-char content word must overlap.
            let overlap = false;
            for (const w of tWords) if (sWords.has(w)) { overlap = true; break; }
            if (overlap) matchedServiceIds.push(svc.id);
          }
        }
      }

      const linksMap = {};
      (links || []).forEach(r => {
        if (!linksMap[r.template_id]) linksMap[r.template_id] = [];
        linksMap[r.template_id].push(r.service_id);
      });

      // Match on either template_id OR normalized name. AR-imported consents
      // may not have the new template_id; we match by name to cover them.
      // ★ NY LAW: medical consents must be re-signed once per year. Anything
      // signed > 365 days ago is treated as expired (not signed) so the
      // patient signs a fresh copy.
      const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const signedIds = new Set();
      const signedNames = new Set();
      (alreadySigned || []).forEach(r => {
        // Skip expired (> 1 year old) signatures — they need to be re-signed.
        const signedAt = r.signed_at ? new Date(r.signed_at).getTime() : 0;
        if (signedAt && (now - signedAt) > ONE_YEAR_MS) return;
        if (r.template_id != null) signedIds.add(String(r.template_id));
        if (r.template_name) signedNames.add(normName(r.template_name));
      });

      const isAlreadySigned = (t) =>
        signedIds.has(String(t.id)) || signedNames.has(normName(t.name));

      const applicableAll = (allTemplates || []).filter(t => {
        const linked = linksMap[t.id] || [];
        // No services assigned → universal consent, always show
        if (linked.length === 0) return t.service_scope !== "specific";
        // Has linked services → only show if appointment includes one of them
        return matchedServiceIds.some(id => linked.includes(id));
      });

      // Split into "needs signature" and "already signed before"
      const stillNeeded = applicableAll.filter(t => !isAlreadySigned(t));
      const previouslySigned = applicableAll.filter(t => isAlreadySigned(t));

      setSkippedAlreadySigned(previouslySigned);
      setTemplates(stillNeeded);
      // Keep the full active-template list for the manual "Add Consent" picker.
      setAllActiveTemplates(allTemplates || []);

      // Split needed into required vs optional. Optional consents (e.g. Social
      // Media Release) get a pre-step where staff can opt the patient in or
      // skip. Required ones get auto-included and we jump straight to signing.
      const required = stillNeeded.filter(t => !t.is_optional);
      const optional = stillNeeded.filter(t => t.is_optional);

      setSelectedIds(required.map(t => t.id));
      if (optional.length > 0) {
        setStep("optional");
      } else if (required.length > 0) {
        setStep("sign");
      } else {
        // ★ Everything is already on file — short-circuit the whole flow.
        // Show a "nothing to sign" screen so staff sees the success message
        // before continuing the check-in.
        setStep("nothing_to_sign");
      }
    })();
  }, [appointment?.id, appointment?.treat, client?.id]);

  const optionalTemplates = templates.filter(t => t.is_optional);
  const requiredTemplates = templates.filter(t => !t.is_optional);

  const selectedTemplates = templates.filter(t => selectedIds.includes(t.id));
  const currentTemplate = selectedTemplates[currentIdx];

  // ★ ADD CONSENT — pull any active template into THIS check-in's signing
  //   queue on demand. If it's not already in `templates` (e.g. it wasn't
  //   matched to the appointment's services), inject it; then select it so
  //   it appears in the flow and gets signed + saved like any other.
  const addConsentToFlow = (tpl) => {
    if (!tpl) return;
    setTemplates(prev => prev.some(t => t.id === tpl.id) ? prev : [...prev, tpl]);
    setSelectedIds(prev => prev.includes(tpl.id) ? prev : [...prev, tpl.id]);
    setShowAddConsent(false);
    setAddSearch("");
    // If we were on a screen with no active signing queue (nothing_to_sign),
    // point currentIdx at the newly-added consent so it renders immediately.
    if (selectedTemplates.length === 0) setCurrentIdx(0);
    // Reset per-form fields so the new consent starts clean.
    setSignature(null);
    setAnswers({});
    setStep("sign");
  };

  // The picker shows active templates not already in the signing queue or
  // already signed this visit. Filtered live by the search box.
  const addPickerList = allActiveTemplates
    .filter(t => !selectedIds.includes(t.id))
    .filter(t => !signedList.some(s => s.template_id === t.id))
    .filter(t => {
      const q = addSearch.trim().toLowerCase();
      if (!q) return true;
      return (t.name || "").toLowerCase().includes(q) || (t.category || "").toLowerCase().includes(q);
    });

  // Reusable picker modal — rendered on every step so "Add Consent" works
  // from the optional screen, the signing screen, and the nothing-to-sign screen.
  const addConsentModal = showAddConsent ? (
    <div onClick={()=>setShowAddConsent(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:460,maxHeight:"80vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:8}}>＋ Add a Consent</div>
          <input
            autoFocus
            value={addSearch}
            onChange={e=>setAddSearch(e.target.value)}
            placeholder="Search consent forms by name…"
            style={{width:"100%",padding:"10px 12px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"}}
          />
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
          {addPickerList.length === 0 ? (
            <div style={{padding:24,textAlign:"center",color:C.muted,fontSize:12}}>
              {addSearch ? `No consent matches "${addSearch}".` : "All active consents are already in this check-in."}
            </div>
          ) : addPickerList.map(t => (
            <button key={t.id} onClick={()=>addConsentToFlow(t)} style={{display:"block",width:"100%",textAlign:"left",padding:"11px 20px",background:"#fff",border:"none",borderBottom:`1px solid ${C.border}`,cursor:"pointer",fontFamily:F}}
              onMouseEnter={e=>e.currentTarget.style.background=C.goldBg}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{t.name}</div>
              {t.category && <div style={{fontSize:10,color:C.muted,marginTop:2,textTransform:"uppercase",letterSpacing:"0.05em"}}>{t.category}</div>}
            </button>
          ))}
        </div>
        <div style={{padding:"12px 20px",borderTop:`1px solid ${C.border}`,textAlign:"right"}}>
          <button onClick={()=>setShowAddConsent(false)} style={{padding:"8px 16px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
        </div>
      </div>
    </div>
  ) : null;

  const addConsentButton = (
    <button onClick={()=>{ setAddSearch(""); setShowAddConsent(true); }} style={{padding:"7px 14px",background:"#fff",color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>
      ＋ Add Consent
    </button>
  );

  // ★ localStorage draft key — per appointment + per template so each
  //   signing session is independently recoverable if the network drops.
  const draftKey = (tplId) => `consent_draft_${appointment?.id || "no-appt"}_${tplId || "no-tpl"}`;

  // Auto-save in-progress signing to localStorage so a network hiccup or
  // accidental tab close doesn't lose the signature + answers + patient name.
  useEffect(() => {
    if (!currentTemplate?.id || !appointment?.id) return;
    if (!signature && !patientName && Object.keys(answers || {}).length === 0) return;
    try {
      localStorage.setItem(draftKey(currentTemplate.id), JSON.stringify({
        signature, patientName, answers, ts: Date.now(),
      }));
    } catch (e) { /* quota or private mode — ignore */ }
  }, [signature, patientName, answers, currentTemplate?.id, appointment?.id]);

  // Restore an unfinished signing session when the template comes into view.
  useEffect(() => {
    if (!currentTemplate?.id || !appointment?.id) return;
    try {
      const raw = localStorage.getItem(draftKey(currentTemplate.id));
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.signature && !signature) setSignature(draft.signature);
      if (draft?.patientName && !patientName) setPatientName(draft.patientName);
      if (draft?.answers && Object.keys(answers || {}).length === 0) setAnswers(draft.answers);
    } catch (e) { /* corrupt draft — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplate?.id, appointment?.id]);

  const handleSignCurrent = async () => {
    if (!signature || !patientName) {
      alert("Please sign and enter patient name");
      return;
    }
    // ★ Canvas-emptiness guard — a transparent/blank canvas can still
    //   produce a non-null data URL. Sample bytes to make sure SOMETHING
    //   was drawn. (Cheap heuristic: blank PNGs are typically tiny.)
    if (typeof signature === "string" && signature.length < 200) {
      alert("Signature looks blank — please sign in the box before saving.");
      return;
    }
    // ★ Client guard — without a client_id the row is orphaned forever.
    if (!client?.id) {
      alert("Cannot save — missing client. Close and reopen the consent dialog from the client's profile.");
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      alert("Not signed in. Log in again before saving the consent.");
      return;
    }
    const { data: prof } = await supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle();

    const signedDoc = {
      template_id: currentTemplate.id,
      template_name: currentTemplate.name,
      client_id: client.id,
      client_name: patientName,
      appointment_id: appointment?.id,
      content: currentTemplate.content,
      answers: answers,
      signatures: { patient: signature, patient_name: patientName, signed_time: new Date().toISOString() },
      signed_by_user: user.id,
      signed_by_name: prof?.full_name || user.email,
      status: "signed",
    };

    const { data, error } = await supabase.from("signed_consents").insert([signedDoc]).select().single();
    if (error) {
      // ★ Do NOT clear `signature` on failure — the user just spent 30s
      //   drawing. They'd have to start over. Surface the error + keep
      //   the canvas data in state + leave the localStorage draft alone
      //   so a retry "just works".
      alert("Save error: " + error.message + "\n\nYour signature is still on the screen — you can retry by clicking Save again.");
      return;
    }

    // Verify the row is actually visible (RLS could mask the insert).
    try {
      const { data: check } = await supabase.from("signed_consents").select("id").eq("id", data.id).maybeSingle();
      if (!check) {
        alert("⚠️ Consent saved but not visible after read-back. This is rare — please flag the IT contact. Continuing for now.");
      }
    } catch (e) { /* read-back is best-effort */ }

    setSignedList([...signedList, data]);
    setSignature(null);
    setAnswers({});

    // Successful save — clear this template's localStorage draft.
    try { localStorage.removeItem(draftKey(currentTemplate.id)); } catch (e) {}

    if (currentIdx < selectedTemplates.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      setStep("complete");
    }
  };

  if (step === "nothing_to_sign") {
    return (
      <div style={{padding:40,textAlign:"center"}}>
        {addConsentModal}
        <div style={{fontSize:48,marginBottom:12}}>✅</div>
        <div style={{fontSize:20,fontWeight:700,color:C.green,fontFamily:S,marginBottom:8}}>All Consents Already on File</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>
          {client?.name || "This client"} already signed the {skippedAlreadySigned.length} consent{skippedAlreadySigned.length!==1?"s":""} needed for {appointment?.treat || "this visit"}.
          <br/>No need to sign again.
        </div>
        {skippedAlreadySigned.length > 0 && (
          <div style={{margin:"16px auto",maxWidth:420,padding:"12px 14px",background:C.greenL,border:`1px solid ${C.green}40`,borderRadius:8,textAlign:"left"}}>
            <div style={{fontSize:11,fontWeight:700,color:C.green,textTransform:"uppercase",marginBottom:6,letterSpacing:"0.05em"}}>On file:</div>
            {skippedAlreadySigned.map(t => (
              <div key={t.id} style={{fontSize:12,color:C.text,padding:"3px 0",display:"flex",alignItems:"center",gap:6}}>
                <span style={{color:C.green}}>✓</span> {t.name}
              </div>
            ))}
          </div>
        )}
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",marginTop:8}}>
          <button onClick={()=>{ setAddSearch(""); setShowAddConsent(true); }} style={{padding:"12px 22px",background:"#fff",color:C.gold,border:`1.5px solid ${C.borderGold}`,borderRadius:8,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F}}>＋ Add a Consent to Sign</button>
          <button onClick={()=>onComplete([])} style={{padding:"12px 28px",background:`linear-gradient(135deg,${C.green},${C.green}cc)`,color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F}}>Continue Check-In →</button>
        </div>
      </div>
    );
  }

  const alreadySignedBanner = skippedAlreadySigned.length > 0 ? (
    <div style={{padding:"10px 14px",background:C.greenL,border:`1px solid ${C.green}40`,borderRadius:8,marginBottom:14,fontSize:12,color:C.green,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:16}}>✓</span>
      <span>{skippedAlreadySigned.length} consent{skippedAlreadySigned.length!==1?"s":""} already on file — skipping ({skippedAlreadySigned.map(t => t.name).join(", ")})</span>
    </div>
  ) : null;

  if (step === "optional") {
    return (
      <div style={{padding:24}}>
        {addConsentModal}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap",marginBottom:6}}>
          <div style={{fontSize:18,fontWeight:700,color:C.gold,fontFamily:S}}>Optional Consents</div>
          {addConsentButton}
        </div>
        <div style={{fontSize:12,color:C.muted,marginBottom:16}}>
          For {client?.name || "client"} · {appointment?.treat || ""}
          <br/>Ask the patient if they'd like to sign any of these. Skip to go straight to the required consents.
        </div>
        {alreadySignedBanner}

        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20,maxHeight:300,overflowY:"auto"}}>
          {optionalTemplates.map(t => {
            const checked = selectedIds.includes(t.id);
            return (
              <label key={t.id} style={{display:"flex",gap:10,padding:"14px 16px",background:checked?C.goldBg:C.light,border:`2px solid ${checked?C.gold:C.border}`,borderRadius:10,cursor:"pointer",alignItems:"flex-start"}}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={()=>setSelectedIds(ids => checked ? ids.filter(i=>i!==t.id) : [...ids,t.id])}
                  style={{accentColor:C.gold,marginTop:3,width:18,height:18}}
                />
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,display:"flex",alignItems:"center",gap:8}}>
                    {t.name}
                    <span style={{padding:"2px 8px",background:C.goldBg,color:C.goldD,fontSize:10,fontWeight:700,borderRadius:10,textTransform:"uppercase",letterSpacing:"0.05em"}}>Optional</span>
                  </div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>{t.category} · {t.questions?.length || 0} questions</div>
                </div>
              </label>
            );
          })}
        </div>

        {requiredTemplates.length > 0 && (
          <div style={{padding:"10px 14px",background:C.greenL,border:`1px solid ${C.green}40`,borderRadius:8,marginBottom:16,fontSize:12,color:C.green,fontWeight:600}}>
            ✓ {requiredTemplates.length} required consent{requiredTemplates.length!==1?"s":""} will be signed after this
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={onCancel} style={{padding:"10px 18px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button
            onClick={()=>{
              // "Skip optionals" — keep only required in selectedIds, then move on
              setSelectedIds(requiredTemplates.map(t => t.id));
              if (requiredTemplates.length > 0) setStep("sign");
              else setStep("complete");
            }}
            style={{padding:"10px 18px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:F}}
          >
            Skip Optional →
          </button>
          <button
            onClick={()=>{
              // Combine selected optionals + all required
              const optIds = selectedIds.filter(id => optionalTemplates.some(t => t.id === id));
              const reqIds = requiredTemplates.map(t => t.id);
              const finalIds = [...optIds, ...reqIds];
              setSelectedIds(finalIds);
              if (finalIds.length > 0) setStep("sign");
              else setStep("complete");
            }}
            style={{padding:"10px 20px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}
          >
            Continue →
          </button>
        </div>
      </div>
    );
  }

  if (step === "select") {
    return (
      <div style={{padding:24}}>
        <div style={{fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:6}}>Select Consents to Sign</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:16}}>For {client?.name || "client"} · {appointment?.treat || ""}</div>
        {alreadySignedBanner}
        
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20,maxHeight:300,overflowY:"auto"}}>
          {templates.map(t => {
            const checked = selectedIds.includes(t.id);
            return (
              <label key={t.id} style={{display:"flex",gap:10,padding:"12px 14px",background:checked?C.goldBg:C.light,border:`1px solid ${checked?C.borderGold:C.border}`,borderRadius:8,cursor:"pointer",alignItems:"flex-start"}}>
                <input type="checkbox" checked={checked} onChange={()=>setSelectedIds(ids => checked ? ids.filter(i=>i!==t.id) : [...ids,t.id])} style={{accentColor:C.gold,marginTop:2}} />
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{t.name}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>{t.category} · {t.questions?.length || 0} questions</div>
                </div>
              </label>
            );
          })}
          {templates.length === 0 && <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:13}}>No consent templates. Create some in the Consent Templates section.</div>}
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}>
          <button onClick={onCancel} style={{padding:"10px 18px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button
            onClick={() => {
              if (!window.confirm("Skip all consents and continue check-in?\n\nThe client will NOT have signed any consents for today. They can sign later if needed.")) return;
              onComplete([]);
            }}
            style={{padding:"10px 18px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:F}}
            title="Skip consents entirely and continue to check the patient in"
          >
            ⏭ Skip consents
          </button>
          <button onClick={()=>selectedIds.length > 0 && setStep("sign")} disabled={selectedIds.length===0} style={{padding:"10px 20px",background:selectedIds.length>0?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,color:selectedIds.length>0?"#fff":C.muted,border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:selectedIds.length>0?"pointer":"not-allowed",fontFamily:F}}>Start Signing ({selectedIds.length}) →</button>
        </div>
      </div>
    );
  }

  if (step === "sign" && currentTemplate) {
    return (
      <div style={{padding:24,maxHeight:"85vh",overflowY:"auto"}}>
        {addConsentModal}
        {currentIdx === 0 && alreadySignedBanner}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:10,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:"0.08em"}}>Consent {currentIdx+1} of {selectedTemplates.length}</div>
            <div style={{fontSize:18,fontWeight:700,color:C.gold,fontFamily:S}}>{currentTemplate.name}</div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            {addConsentButton}
            <div style={{display:"flex",gap:4}}>
              {selectedTemplates.map((_,i)=>(
                <div key={i} style={{width:24,height:4,borderRadius:2,background:i<currentIdx?C.green:i===currentIdx?C.gold:C.border}}/>
              ))}
            </div>
          </div>
        </div>

        <div style={{padding:"14px 16px",background:C.light,borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,lineHeight:1.6,color:C.text,marginBottom:16,maxHeight:180,overflowY:"auto"}}>
          {currentTemplate.content}
        </div>

        {currentTemplate.questions?.length > 0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Questions</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {currentTemplate.questions.map(q => (
                <div key={q.id} style={{padding:"10px 12px",background:C.light,borderRadius:8,border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:6}}>{q.text}{q.required && <span style={{color:C.red}}> *</span>}</div>
                  {q.type === "yes_no" ? (
                    <div style={{display:"flex",gap:8}}>
                      {["Yes","No"].map(opt => (
                        <button key={opt} onClick={()=>setAnswers({...answers,[q.id]:opt})} style={{padding:"6px 14px",fontSize:12,fontWeight:600,background:answers[q.id]===opt?C.gold:"#fff",color:answers[q.id]===opt?"#fff":C.text,border:`1px solid ${answers[q.id]===opt?C.gold:C.border}`,borderRadius:6,cursor:"pointer",fontFamily:F}}>{opt}</button>
                      ))}
                    </div>
                  ) : (
                    <input value={answers[q.id]||""} onChange={e=>setAnswers({...answers,[q.id]:e.target.value})} style={{...inp,background:"#fff"}} placeholder="Answer..." />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Patient Name</div>
          <input value={patientName} onChange={e=>setPatientName(e.target.value)} style={inp} placeholder="Full name" />
        </div>

        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Patient Signature</div>
          {/* Force a fresh SignaturePad for every consent by keying on the
              template id. Without this, React reuses the same component
              instance and the canvas keeps the previous signature drawn on it
              even though the parent has reset `signature` to null. */}
          <SignaturePad key={currentTemplate?.id || currentIdx} onSign={setSignature} />
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}>
          <button onClick={()=>setStep("select")} style={{padding:"10px 18px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>← Back</button>
          {/* ★ Skip ONLY the current consent and advance to the next one.
              Owner request 2026-06-10: sometimes one specific form isn't
              relevant today but the rest still need signing. "Skip rest"
              (below) stays for bailing out of everything at once. */}
          <button
            onClick={() => {
              const name = currentTemplate?.name || "this consent";
              if (!window.confirm(`Skip "${name}" only?\n\nIt won't be signed today — you can sign it later from the client's profile. The next consent will open.`)) return;
              // Clear per-template state + its draft so nothing leaks into the next form
              setSignature(null);
              setAnswers({});
              try { localStorage.removeItem(draftKey(currentTemplate?.id)); } catch (e) {}
              if (currentIdx < selectedTemplates.length - 1) {
                setCurrentIdx(currentIdx + 1);
              } else if (signedList.length > 0) {
                setStep("complete");
              } else {
                onComplete(signedList);
              }
            }}
            style={{padding:"10px 18px",background:"#fff",color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:F}}
            title="Skip just this consent and move to the next one"
          >
            ↷ Skip this one
          </button>
          <button
            onClick={() => {
              if (!window.confirm("Skip the remaining consents and finish check-in?\n\nSigned so far will be kept. The skipped ones can be signed later.")) return;
              onComplete(signedList);
            }}
            style={{padding:"10px 18px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:F}}
            title="Stop signing and continue check-in. Already-signed are kept; remaining are skipped."
          >
            ⏭ Skip rest
          </button>
          <button onClick={handleSignCurrent} style={{padding:"10px 20px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Sign & {currentIdx < selectedTemplates.length - 1 ? "Next →" : "Finish ✓"}</button>
        </div>
      </div>
    );
  }

  if (step === "complete") {
    return (
      <div style={{padding:40,textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:12}}>✅</div>
        <div style={{fontSize:20,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:8}}>All Consents Signed!</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:20}}>{signedList.length} document(s) signed and saved</div>
        <button onClick={()=>onComplete(signedList)} style={{padding:"12px 28px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F}}>Done - Check In Patient</button>
      </div>
    );
  }

  return null;
}

// Consent Templates Manager (CRUD)
export function ConsentTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [services, setServices] = useState([]);
  const [linkRows, setLinkRows] = useState([]);
  const [editingServiceIds, setEditingServiceIds] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");

  const load = async () => {
    const [{ data: t }, { data: s }, { data: l }] = await Promise.all([
      supabase.from("consent_templates").select("*").order("sort_order"),
      supabase.from("services").select("id,name,category").eq("active", true).order("name"),
      supabase.from("consent_template_services").select("*"),
    ]);
    setTemplates(t || []);
    setServices(s || []);
    setLinkRows(l || []);
  };

  useEffect(() => { load(); }, []);

  const openEditor = (tpl) => {
    setEditing(tpl);
    const linked = (linkRows || []).filter(r => r.template_id === tpl.id).map(r => r.service_id);
    setEditingServiceIds(linked);
    setServiceSearch("");
    setShowEditor(true);
  };

  const save = async (template) => {
    // Strip fields that don't belong in the templates table
    const { id, ...rest } = template;
    let templateId = id;
    if (id) {
      await supabase.from("consent_templates").update({
        ...rest,
        service_scope: editingServiceIds.length > 0 ? "specific" : "all",
      }).eq("id", id);
    } else {
      const { data } = await supabase.from("consent_templates").insert([{
        ...rest,
        service_scope: editingServiceIds.length > 0 ? "specific" : "all",
      }]).select().single();
      templateId = data?.id;
    }

    // Rewrite link rows: clear + reinsert
    if (templateId) {
      await supabase.from("consent_template_services").delete().eq("template_id", templateId);
      if (editingServiceIds.length > 0) {
        await supabase.from("consent_template_services").insert(
          editingServiceIds.map(sid => ({ template_id: templateId, service_id: sid }))
        );
      }
    }

    setShowEditor(false);
    setEditing(null);
    setEditingServiceIds([]);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this consent template?")) return;
    await supabase.from("consent_templates").delete().eq("id", id);
    load();
  };

  const move = async (id, direction) => {
    const idx = templates.findIndex(t => t.id === id);
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= templates.length) return;
    const a = templates[idx], b = templates[newIdx];
    await supabase.from("consent_templates").update({ sort_order: b.sort_order }).eq("id", a.id);
    await supabase.from("consent_templates").update({ sort_order: a.sort_order }).eq("id", b.id);
    load();
  };

  return (
    <div style={{padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:S}}>Consent Templates</div>
          <div style={{fontSize:11,color:C.muted}}>Create and manage consent forms</div>
        </div>
        <button onClick={()=>openEditor({name:"",category:"General",content:"",questions:[],sort_order:templates.length,active:true})} style={{padding:"9px 18px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ New Template</button>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {templates.map((t, i) => (
          <div key={t.id} style={{padding:"12px 14px",background:C.card,border:`1px solid ${C.border}`,borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
            <div style={{display:"flex",flexDirection:"column",gap:2}}>
              <button onClick={()=>move(t.id,"up")} disabled={i===0} style={{padding:"2px 6px",background:"none",border:"none",cursor:i===0?"not-allowed":"pointer",color:i===0?C.border:C.muted,fontSize:10}}>▲</button>
              <button onClick={()=>move(t.id,"down")} disabled={i===templates.length-1} style={{padding:"2px 6px",background:"none",border:"none",cursor:i===templates.length-1?"not-allowed":"pointer",color:i===templates.length-1?C.border:C.muted,fontSize:10}}>▼</button>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,display:"flex",alignItems:"center",gap:6}}>
                {t.name}
                {t.is_optional && <span style={{padding:"1px 7px",background:C.goldBg,color:C.goldD,fontSize:9,fontWeight:700,borderRadius:8,textTransform:"uppercase",letterSpacing:"0.05em"}}>Optional</span>}
              </div>
              <div style={{fontSize:11,color:C.muted}}>
                {t.category} · {t.questions?.length || 0} questions
                {(() => {
                  const cnt = (linkRows || []).filter(r => r.template_id === t.id).length;
                  return cnt > 0
                    ? <span style={{color:C.gold,fontWeight:600}}> · 🔗 {cnt} service{cnt!==1?"s":""}</span>
                    : <span style={{color:C.green,fontWeight:600}}> · 🌐 Universal</span>;
                })()}
                {!t.active && " · INACTIVE"}
              </div>
            </div>
            <button onClick={()=>openEditor({...t})} style={{padding:"6px 12px",fontSize:11,fontWeight:600,background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:6,cursor:"pointer",fontFamily:F}}>Edit</button>
            <button onClick={()=>del(t.id)} style={{padding:"6px 12px",fontSize:11,fontWeight:600,background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:6,cursor:"pointer",fontFamily:F}}>🗑️</button>
          </div>
        ))}
      </div>

      {showEditor && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
          <div style={{background:C.surface,borderRadius:16,width:"100%",maxWidth:600,maxHeight:"90vh",overflow:"auto",padding:24}}>
            <div style={{fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:16}}>{editing.id ? "Edit" : "New"} Consent Template</div>
            
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>Name *</label>
                <input value={editing.name} onChange={e=>setEditing({...editing,name:e.target.value})} style={inp} placeholder="e.g. Botox Treatment Consent" />
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>Category</label>
                <select value={editing.category} onChange={e=>setEditing({...editing,category:e.target.value})} style={inp}>
                  <option>General</option><option>Injectables</option><option>Laser</option><option>Aesthetic</option><option>Medical</option><option>HIPAA</option>
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>Consent Text *</label>
                <textarea value={editing.content} onChange={e=>setEditing({...editing,content:e.target.value})} rows={6} style={{...inp,resize:"vertical"}} placeholder="Full consent text..." />
              </div>
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase"}}>Questions</label>
                  <button onClick={()=>setEditing({...editing,questions:[...(editing.questions||[]),{id:"q"+Date.now(),text:"",type:"yes_no",required:false}]})} style={{padding:"4px 10px",fontSize:11,background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:6,cursor:"pointer",fontFamily:F}}>+ Add Question</button>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {(editing.questions||[]).map((q,i)=>(
                    <div key={q.id} style={{padding:"10px",background:C.light,borderRadius:6,border:`1px solid ${C.border}`}}>
                      <div style={{display:"flex",gap:6,marginBottom:6}}>
                        <input value={q.text} onChange={e=>{const qs=[...editing.questions];qs[i]={...q,text:e.target.value};setEditing({...editing,questions:qs});}} placeholder="Question text..." style={{...inp,flex:1,fontSize:12}} />
                        <select value={q.type} onChange={e=>{const qs=[...editing.questions];qs[i]={...q,type:e.target.value};setEditing({...editing,questions:qs});}} style={{...inp,width:"auto",fontSize:12}}>
                          <option value="yes_no">Yes/No</option><option value="text">Text</option>
                        </select>
                        <button onClick={()=>setEditing({...editing,questions:editing.questions.filter((_,j)=>j!==i)})} style={{padding:"0 10px",background:C.redL,color:C.red,border:"none",borderRadius:6,cursor:"pointer"}}>×</button>
                      </div>
                      <label style={{fontSize:11,color:C.muted,display:"flex",gap:4,alignItems:"center"}}>
                        <input type="checkbox" checked={q.required||false} onChange={e=>{const qs=[...editing.questions];qs[i]={...q,required:e.target.checked};setEditing({...editing,questions:qs});}} style={{accentColor:C.gold}} />
                        Required
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>
                  Applies To Services
                  <span style={{color:C.muted,fontWeight:400,marginLeft:6,textTransform:"none",letterSpacing:0}}>
                    (leave empty = universal, shows for every appointment)
                  </span>
                </label>

                <div style={{padding:"10px 12px",background:editingServiceIds.length>0?C.goldBg:C.greenL,border:`1px solid ${editingServiceIds.length>0?C.borderGold:C.green}40`,borderRadius:8,marginBottom:8,fontSize:12,color:editingServiceIds.length>0?C.goldD:C.green,fontWeight:600}}>
                  {editingServiceIds.length > 0
                    ? `🔗 Linked to ${editingServiceIds.length} service${editingServiceIds.length!==1?"s":""} — only shows on appointments with those`
                    : `🌐 Universal — shows on every appointment check-in`}
                </div>

                <input
                  value={serviceSearch}
                  onChange={e=>setServiceSearch(e.target.value)}
                  placeholder="🔍 Search services..."
                  style={{...inp,marginBottom:8}}
                />

                {editingServiceIds.length > 0 && (
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                    {editingServiceIds.map(sid => {
                      const s = services.find(x => x.id === sid);
                      if (!s) return null;
                      return (
                        <span key={sid} style={{padding:"4px 10px",background:C.gold,color:"#fff",borderRadius:12,fontSize:11,fontWeight:600,display:"inline-flex",alignItems:"center",gap:6}}>
                          {s.name}
                          <button onClick={()=>setEditingServiceIds(editingServiceIds.filter(x => x !== sid))} style={{background:"rgba(255,255,255,0.3)",border:"none",borderRadius:"50%",width:16,height:16,color:"#fff",cursor:"pointer",fontSize:10,lineHeight:1,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                        </span>
                      );
                    })}
                    <button onClick={()=>setEditingServiceIds([])} style={{padding:"4px 10px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:12,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Clear all</button>
                  </div>
                )}

                <div style={{maxHeight:220,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8,padding:6}}>
                  {(() => {
                    const q = serviceSearch.trim().toLowerCase();
                    const filtered = q
                      ? services.filter(s => s.name.toLowerCase().includes(q) || (s.category||"").toLowerCase().includes(q))
                      : services;
                    if (filtered.length === 0) return <div style={{padding:12,textAlign:"center",color:C.muted,fontSize:12}}>No services match</div>;
                    return filtered.map(s => {
                      const checked = editingServiceIds.includes(s.id);
                      return (
                        <label key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:6,cursor:"pointer",background:checked?C.goldBg:"transparent"}}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={()=>setEditingServiceIds(checked ? editingServiceIds.filter(x=>x!==s.id) : [...editingServiceIds, s.id])}
                            style={{accentColor:C.gold}}
                          />
                          <span style={{flex:1,fontSize:12,color:C.text}}>{s.name}</span>
                          {s.category && <span style={{fontSize:10,color:C.muted}}>{s.category}</span>}
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>

              <label style={{fontSize:12,display:"flex",gap:6,alignItems:"center",color:C.text}}>
                <input type="checkbox" checked={editing.active!==false} onChange={e=>setEditing({...editing,active:e.target.checked})} style={{accentColor:C.gold}} />
                Active (available for signing)
              </label>

              <label style={{fontSize:12,display:"flex",gap:8,alignItems:"flex-start",padding:"10px 12px",background:editing.is_optional?C.goldBg:C.light,border:`1px solid ${editing.is_optional?C.borderGold:C.border}`,borderRadius:8,cursor:"pointer"}}>
                <input type="checkbox" checked={!!editing.is_optional} onChange={e=>setEditing({...editing,is_optional:e.target.checked})} style={{accentColor:C.gold,marginTop:2}} />
                <div>
                  <div style={{fontWeight:700,color:C.text}}>Optional — ask before signing</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>Patient gets a chance to opt in or skip (use for Social Media Release, marketing photos, etc.)</div>
                </div>
              </label>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20}}>
              <button onClick={()=>{setShowEditor(false);setEditing(null);setEditingServiceIds([]);}} style={{padding:"10px 18px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={()=>save(editing)} disabled={!editing.name||!editing.content} style={{padding:"10px 20px",background:editing.name&&editing.content?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,color:editing.name&&editing.content?"#fff":C.muted,border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:editing.name&&editing.content?"pointer":"not-allowed",fontFamily:F}}>Save Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Client History Viewer
export function ClientHistory({ client }) {
  const [consents, setConsents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!client?.id) return;
    const load = () => {
      supabase.from("signed_consents").select("*").eq("client_id", client.id).order("signed_at", { ascending: false }).then(({data})=>setConsents(data||[]));
      supabase.from("appointments").select("*").eq("cid", client.id).order("date", { ascending: false }).then(({data})=>setAppointments(data||[]));
      supabase.from("checkins").select("*").eq("client_id", client.id).order("checked_in_at", { ascending: false }).then(({data})=>setCheckins(data||[]));
    };
    load();
    // Live updates so newly-signed consents/check-ins appear without a manual
    // refresh. Without this, staff signs a consent in another tab/flow and
    // the Consents tab on the client profile still shows the stale (empty)
    // list until they navigate away and back.
    const ch = supabase.channel("client-consents-" + client.id)
      .on("postgres_changes", { event: "*", schema: "public", table: "signed_consents", filter: "client_id=eq." + client.id }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "checkins",        filter: "client_id=eq." + client.id }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [client]);

  return (
    <div>
      <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>📋 Signed Consents ({consents.length})</div>
      {consents.length === 0 && <div style={{padding:12,color:C.muted,fontSize:12}}>No signed consents yet</div>}
      {consents.map(c => (
        <div key={c.id} style={{padding:"10px 12px",background:C.light,borderRadius:8,marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:700,color:C.text}}>{c.template_name}</div>
            <div style={{fontSize:11,color:C.muted}}>Signed {new Date(c.signed_at).toLocaleString()} by {c.signed_by_name}</div>
          </div>
          <button onClick={()=>setViewing(c)} style={{padding:"5px 11px",fontSize:11,fontWeight:600,background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:6,cursor:"pointer",fontFamily:F}}>View</button>
        </div>
      ))}

      <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginTop:20,marginBottom:10}}>🔄 Check-in History ({checkins.length})</div>
      {checkins.length === 0 && <div style={{padding:12,color:C.muted,fontSize:12}}>No check-ins yet</div>}
      {checkins.map(c => (
        <div key={c.id} style={{padding:"10px 12px",background:C.light,borderRadius:8,marginBottom:6}}>
          <div style={{fontSize:12,fontWeight:700,color:C.text}}>Check-in: {new Date(c.checked_in_at).toLocaleString()}</div>
          <div style={{fontSize:11,color:C.muted}}>By {c.checked_in_by_name} {c.checked_out_at && `· Out: ${new Date(c.checked_out_at).toLocaleString()}`}</div>
          {c.consents_signed?.length > 0 && <div style={{fontSize:11,color:C.green,marginTop:3}}>✓ {c.consents_signed.length} consent(s) signed</div>}
        </div>
      ))}

      {viewing && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:12,maxWidth:600,width:"100%",maxHeight:"85vh",overflow:"auto",padding:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:18,fontWeight:700,color:C.gold,fontFamily:S}}>{viewing.template_name}</div>
              <button onClick={()=>setViewing(null)} style={{background:C.light,border:"none",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:16}}>×</button>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:10}}>Signed by {viewing.client_name} on {new Date(viewing.signed_at).toLocaleString()}</div>
            <div style={{padding:14,background:C.light,borderRadius:8,fontSize:13,lineHeight:1.6,marginBottom:14}}>{viewing.content}</div>
            {viewing.answers && Object.keys(viewing.answers).length > 0 && (
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>Answers</div>
                {Object.entries(viewing.answers).map(([k,v])=>(
                  <div key={k} style={{fontSize:12,padding:"4px 0"}}><strong>{k}:</strong> {v}</div>
                ))}
              </div>
            )}
            {viewing.signatures?.patient && (
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",marginBottom:6}}>Signature</div>
                <img src={viewing.signatures.patient} alt="sig" style={{maxWidth:300,border:`1px solid ${C.border}`,borderRadius:6,background:"#fff"}} />
                <div style={{fontSize:11,color:C.muted,marginTop:4}}>{viewing.signatures.patient_name}</div>
              </div>
            )}
            <div style={{marginTop:16,display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap"}}>
              {(() => {
                // Shared HTML used by both buttons. Built once per render of
                // the modal so View and Download render the same document.
                const fullName = (viewing.client_name || "").replace(/[<>]/g, "");
                const safeName = `${viewing.template_name}_${viewing.client_name}`.replace(/[^a-z0-9_]+/gi, "_");
                const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${viewing.template_name}</title>
<style>
  @page { margin: 20mm; }
  body { font-family: Georgia, serif; padding: 20px; max-width: 720px; margin: auto; color: #1a1a2e; background: #fff; }
  h1 { color: #b48c3c; border-bottom: 2px solid #b48c3c; padding-bottom: 10px; font-family: 'Playfair Display', Georgia, serif; }
  .meta { color: #666; font-size: 13px; margin: 10px 0 20px; }
  .content { line-height: 1.7; margin: 20px 0; padding: 20px; background: #f8f5f0; border-radius: 8px; border: 1px solid #e8e4df; white-space: pre-wrap; }
  .answers { margin: 20px 0; }
  .answer { padding: 8px 0; border-bottom: 1px solid #ddd; font-size: 13px; }
  .sig-box { margin-top: 30px; padding-top: 20px; border-top: 2px solid #b48c3c; }
  .sig-img { max-width: 320px; border: 1px solid #ddd; padding: 10px; background: #fff; }
  h3 { color: #b48c3c; }
  .print-btn { background: #b48c3c; color: #fff; border: none; padding: 10px 22px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; }
  @media print { .no-print { display: none; } }
</style></head><body>
<h1>${viewing.template_name}</h1>
<div class="meta">Patient: <strong>${fullName}</strong><br/>Date signed: ${new Date(viewing.signed_at).toLocaleString()}<br/>Signed by: ${(viewing.signed_by_name||"").replace(/[<>]/g,"")}</div>
<div class="content">${(viewing.content||"").replace(/[<>]/g, "")}</div>
${Object.keys(viewing.answers||{}).length ? `<div class="answers"><h3>Questions &amp; Answers</h3>${Object.entries(viewing.answers).map(([k,v]) => `<div class="answer"><strong>${k}:</strong> ${v}</div>`).join("")}</div>` : ""}
${viewing.signatures?.patient ? `<div class="sig-box"><h3>Signature</h3><img class="sig-img" src="${viewing.signatures.patient}" alt="signature"/><div class="meta">${viewing.signatures.patient_name || ""}</div></div>` : ""}
<div class="no-print" style="margin-top:30px;text-align:center;"><button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button></div>
</body></html>`;

                const openInNewTab = () => {
                  const blob = new Blob([html], { type: "text/html" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                };

                const downloadHtml = () => {
                  const blob = new Blob([html], { type: "text/html" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `${safeName}.html`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                };

                return (
                  <>
                    <button onClick={downloadHtml} style={{padding:"8px 16px",background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>📥 Download HTML</button>
                    <button onClick={openInNewTab} style={{padding:"8px 16px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>👁 View / Print PDF</button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
